import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as ReactDOM from "react-dom/client";
import { useInitializeMap } from "./hooks/useInitializeMap";
import { GaiaMapRibbon } from "./components/GaiaMapRibbon";
import "./GaiaMap.css";
import { Box, Typography } from "@mui/material";
import { GaiaMapContext, GaiaMapProvider } from "./hooks/useGaiaMapContext";
import { GaiaMapControls } from "./components/GaiaMapControls";
import { GaiaMapPrintPreview } from "./components/GaiaMapPrintPreview";
import L, { Layer, Polyline } from "leaflet";
import { useMapHistory } from "./hooks/useMapHistory";
import { GetBoundaryRequest, GetBoundaryResponse } from "@shared/mirage/core/contracts/GetBoundaryRequest";
import { ClonePreviousGeometryRequest, ClonePreviousGeometryResponse } from "@shared/planningmanagement/contracts/IClonePreviousGeometry";
import { GeometryProjector } from "@shared/mirage/utils/GeometryProjector";
import { union as turfUnion, difference as turfDifference, unkinkPolygon as turfUnkink, intersect as turfIntersect } from "@turf/turf";
import { MultiLineString, Feature, Polygon, LineString, polygon as turfPolygon } from "@turf/helpers";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { useFeatureGroupToWorkingGeometry } from "./hooks/useFeatureGroupToWorkingGeometry";
import { MapViewHelper, ColorPalette } from "../MapView/MapViewHelper";
import MapMarkersPane from "../MapView/components/MapMarkersPane";
import { MapMarkersGroup } from "../MapView/MapView";
import { useGaiaMapTheme } from "./hooks/useGaiaMapTheme";
import { GetFeatureInfoRequest, GetFeatureInfoResponse, WmsCapabilitiesResponse, WmsLayer } from "@shared/mirage/services/WmsCapabilities";
import { GetWmsCapabilitiesRequest } from "@shared/mirage/services/WmsCapabilities";
import Field, { MirageOnChangeEvent } from "../regularField/Field";
import { Legend } from "../MapView/components/MapViewLegend";
import { EarthlightMapBoxSize, PrintMapRequest } from "@shared/mirage/services/IEarthlightService";
import { useBoolean } from "@mirage/hooks/useBoolean";
import useAlertService from "@mirage/hooks/useAlertService";
import "./WMS/NonTiledLayer";
import "./GaiaMap.css";
import { useClonePreviousGeometry } from "./hooks/useClonePreviousGeometry";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { GeometryOutlinePattern, GeometryOutlinePatterns } from "@shared/mirage/core/contracts/IMapView";
import { IErrorMessage } from "@shared/mirage/core/contracts/IErrorMessage";

function turfSymDiff(a: any, b: any): any {
    try {
        const aMinusB = turfDifference(a as any, b as any);
        const bMinusA = turfDifference(b as any, a as any);
        if (aMinusB && bMinusA) return turfUnion(aMinusB as any, bMinusA as any);
        return aMinusB || bMinusA || null;
    } catch {
        try {
            return turfUnion(a as any, b as any);
        } catch {
            return a;
        }
    }
}

const geometryOutlinePatternToDashArray = (pattern?: GeometryOutlinePattern): number[] | undefined => {
    switch (pattern) {
        case GeometryOutlinePatterns.DASHED:
            return [5, 5];
        case GeometryOutlinePatterns.DOTTED:
            return [1, 1];
        case GeometryOutlinePatterns.SOLID:
            return [0, 0];
        default:
            return undefined;
    }
};

export interface TileLayerOptions {
    layerUrl: string;
    attribution?: string;
}

export interface GaiaMapWmsLayer {
    url?: string | null;
    showLegend?: boolean;
    getWmsCapabilities?: (req: GetWmsCapabilitiesRequest) => Promise<WmsCapabilitiesResponse>;
    showLayers?: "all" | string[];
    onShownLayersChanged?: (layers: string[]) => void;
}
export interface GaiaMapGeometryLabel {
    recordId?: number;
    text: string;
    /**
     * @description Label position in EPSG:27700 coordinates [X, Y]
     */
    position?: [number, number];
}

export interface GaiaMapReadOnlyLayer {
    id: number;
    geometryJson: string | null;
    outlineColor?: string;
    fillColor?: string;
    outlinePattern?: GeometryOutlinePattern;
    popupContent?: React.ReactNode;
    zIndexOffset?: number;
    label?: GaiaMapGeometryLabel;
}

export type EnabledToolTypes = "polygon" | "polyline" | "point" | "delete" | "circle" | "selection" | "inspect" | "edit" | "labels" | "clonePrevious";

export interface GaiaMapProps {
    tileLayer?: TileLayerOptions;
    onClone?: (req: GetBoundaryRequest) => Promise<GetBoundaryResponse>;
    center?: L.LatLngExpression | null;
    /**
     * @description GeoJSON string representation in 27700 projection
     */
    geometryJson?: string | null;
    /**
     * @description When true, disables all edit actions (drawing, selecting, deleting).
     */
    readOnly?: boolean;
    /**
     * @description Specifies which tool types should be enabled
     */
    enabledToolTypes?: EnabledToolTypes[];
    singleGeometryMode?: boolean;
    /**
     * @description Callback function that is called when the geometry changes
     * @param geometry - The geometry of the map in 27700 projection
     * @param lastDrawnGeometry - The last drawn geometry of the map FeatureCollection<Geometry, GeoJsonProperties> reprojected to 27700
     */
    onChange?: (geometry: string | null, lastDrawnGeometry: string | null) => void;
    /**
     * @description Callback function that is called when a selection is made
     * @param drawnPolygon - The drawn polygon in GeoJSON format (WGS84)
     * @param actionType - The type of selection action ("select" | "deselect")
     */
    onSelect?: (drawnPolygon: GeoJSON.Feature<GeoJSON.Polygon>, actionType: "select" | "deselect") => void;
    clusterMarkersGroup?: MapMarkersGroup;
    /**
     * @description Custom buttons to be added to the ribbon
     */
    customRibbonButtons?: React.ReactElement[];
    disableUndoRedo?: boolean;
    disableAcceptDrawing?: boolean;
    /**
     * @description Override default colors for map elements
     */
    colorPalette?: ColorPalette;
    wms?: GaiaMapWmsLayer;
    getFeatureInfo?: (req: GetFeatureInfoRequest) => Promise<GetFeatureInfoResponse>;
    onSnapshotRequest?: (req: PrintMapRequest) => Promise<void>;
    onMapBoxSizeRequest?: () => Promise<EarthlightMapBoxSize>;
    pointCustomIcon?: L.Icon;
    pointAsCircleMarker?: boolean;
    /**
     * @description Height of the map in pixels
     */
    height?: number;
    /**
     * @description Zoom level of the map
     */
    zoom?: number;
    readonlyLayers?: GaiaMapReadOnlyLayer[];
    updateGeometryLabel?: (updates: { id: number; label: string }[]) => Promise<IErrorMessage>;
    /**
     * @description Label for the currently editing geometry
     */
    geometryLabel?: GaiaMapGeometryLabel;
    /**
     * @description Callback to fetch previous geometries within the current map viewport
     */
    onClonePreviousGeometry?: (req: ClonePreviousGeometryRequest) => Promise<ClonePreviousGeometryResponse>;
}
export const GAIA_DRAW_MODE = {
    MARKER: "marker",
    POLYLINE: "polyline",
    POLYGON: "polygon",
    SUBTRACT: "subtract",
    CLONE: "clone",
    DELETE: "delete",
    CIRCLE: "circle",
    INSPECT: "inspect",
    RULER: "ruler",
    SELECT: "select",
    DESELECT: "deselect",
    EDIT: "edit",
    LABELS: "labels",
    CLONE_PREVIOUS: "clonePrevious",
} as const;
export type GaiaDrawModeType = typeof GAIA_DRAW_MODE[keyof typeof GAIA_DRAW_MODE] | null;
export type MapWmsLayer = {
    layer: WmsLayer;
    selected: boolean;
};
export const TOOL_TYPE_TO_DRAW_MODES = {
    polygon: [GAIA_DRAW_MODE.CLONE, GAIA_DRAW_MODE.POLYGON, GAIA_DRAW_MODE.SUBTRACT],
    polyline: [GAIA_DRAW_MODE.POLYLINE],
    point: [GAIA_DRAW_MODE.MARKER],
    delete: [GAIA_DRAW_MODE.DELETE],
    circle: [GAIA_DRAW_MODE.CIRCLE],
    selection: [GAIA_DRAW_MODE.SELECT, GAIA_DRAW_MODE.DESELECT],
    edit: [GAIA_DRAW_MODE.EDIT],
    labels: [GAIA_DRAW_MODE.LABELS],
    clonePrevious: [GAIA_DRAW_MODE.CLONE_PREVIOUS],
} as const;

const GaiaMapContent = memo(
    ({
        map,
        drawModeRef,
        points,
        featureGroup,
        tempCircle,
    }: {
        map: L.DrawMap | null;
        drawModeRef: React.MutableRefObject<GaiaDrawModeType>;
        points: React.MutableRefObject<L.LatLng[]>;
        featureGroup: React.MutableRefObject<L.FeatureGroup>;
        tempCircle: React.MutableRefObject<L.GeoJSON<any> | null>;
    }) => {
        const { useInitializeDrawing } = require("./hooks/useInitializeDrawing");
        useInitializeDrawing({ map, drawModeRef, points, featureGroup, tempCircle });
        return null;
    }
);

export const GaiaMap = memo((props: GaiaMapProps) => {
    const {
        tileLayer,
        onClone,
        center,
        geometryJson,
        readOnly = false,
        singleGeometryMode = false,
        enabledToolTypes,
        onChange,
        onSelect,
        clusterMarkersGroup,
        customRibbonButtons,
        disableUndoRedo = false,
        disableAcceptDrawing = false,
        colorPalette,
        wms,
        getFeatureInfo,
        onSnapshotRequest,
        onMapBoxSizeRequest,
        pointCustomIcon,
        pointAsCircleMarker,
        height,
        zoom,
        readonlyLayers,
        updateGeometryLabel,
        geometryLabel,
        onClonePreviousGeometry,
    } = props;
    const [mapBoxSize, setMapBoxSize] = useState<EarthlightMapBoxSize | null>(null);
    const [drawMode, setDrawMode] = useState<GaiaDrawModeType>(null);
    const internalClusterMarkersGroup = clusterMarkersGroup || {
        markers: [],
        clustered: false,
    };
    const [isFullScreen, setIsFullScreen] = useState(false);
    const showAlert = useAlertService();
    const wmsUrl = wms?.url || undefined;
    const gaiaMap = useRef<HTMLDivElement>(null);
    const points = useRef<L.LatLng[]>([]);
    const { value: printLoading, setTrue: startPrintLoading, setFalse: stopPrintLoading } = useBoolean(false);
    const featureGroup = useRef<L.FeatureGroup>(new L.FeatureGroup());
    const [workingGeometry, setWorkingGeometry] = useState<string | null>(null);
    const [wmsLayers, setWmsLayers] = useState<MapWmsLayer[]>([]);
    const [wmsLegendHeader, setWmsLegendHeader] = useState<string | null>(null);
    const [isPrintPreviewActive, setIsPrintPreviewActive] = useState(false);
    const drawModeRef = useRef(drawMode);
    const mapContainer = useRef<HTMLDivElement>(null);
    const readonlyLayerGroup = useRef<L.LayerGroup>(new L.LayerGroup());
    const readonlyLayerPanes = useRef<Map<number, HTMLElement>>(new Map());
    const map = useInitializeMap({ mapContainer, tileLayer, featureGroup, center, wms, setWmsLayers, setWmsLegendHeader, zoom });
    const isReadonlyLayer = useCallback((layer: any) => {
        if (!layer) {
            return false;
        }
        if ((layer as any)._gaiaRole === "readonly" || (layer as any)._readonly === true || (layer.options?.className || "").toString().includes("readonly-layer")) {
            return true;
        }
        if (readonlyLayerGroup.current.hasLayer(layer)) {
            return true;
        }
        return false;
    }, []);

    const mapExists = !!map;
    const showSnapshotButton = !!onSnapshotRequest && !!onMapBoxSizeRequest;
    const { pushHistory, getUndoState, getRedoState, canUndo, canRedo } = useMapHistory();
    const [wmsLayer, setWmsLayer] = useState<any>(null);
    const selectedWmsLayersCount = wmsLayers.filter((l) => l.selected).length;
    const [hasGeometry, setHasGeometry] = useState(false);
    const hasLabels = useMemo(() => {
        return (readonlyLayers || []).some((layer) => layer.label?.text) || !!geometryLabel?.text;
    }, [readonlyLayers, geometryLabel]);
    const labelPositionsSnapshot = useRef<Map<number, L.LatLng>>(new Map());
    const labelMarkersRef = useRef<Map<number, L.Marker>>(new Map());
    const tempCircle = useRef<L.GeoJSON<any> | null>(null);
    const centerMarker = useRef<L.Marker | null>(null);
    const [currentCenter, setCurrentCenter] = useState<L.LatLngExpression | null>(center || null);
    const updateWorkingGeometry = useCallback((geoJsonString: string | null) => setWorkingGeometry(geoJsonString), []);
    const featureGroupToWorkingGeometry = useFeatureGroupToWorkingGeometry(updateWorkingGeometry);
    const makeMarkerNonInteractive = useCallback(() => {
        if (!centerMarker.current) {
            return;
        }

        const markerElement = centerMarker.current.getElement();
        if (markerElement) {
            markerElement.style.pointerEvents = "none";
            markerElement.style.cursor = "default";
            markerElement.classList.add("non-interactive-marker");
        }

        centerMarker.current.options.interactive = false;
    }, []);

    const updateCenterMarker = useCallback(
        (center: L.LatLngExpression) => {
            if (!map) {
                return;
            }

            if (centerMarker.current) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }

            if (featureGroup.current.getLayers().length > 0) {
                return;
            }

            if (readonlyLayerGroup.current.getLayers().length > 0) {
                return;
            }

            const redIcon = L.icon({
                iconUrl:
                    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSI0MSIgdmlld0JveD0iMCAwIDI1IDQxIj48cGF0aCBmaWxsLW9wYWNpdHk9Ii45IiBmaWxsPSIjRUUyMjIwIiBkPSJNMTIgMkM4LjE0IDIgNSA1LjE0IDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ni0zLjE0LTctNy03em0wIDlhMiAyIDAgMSAwIDAtNCAyIDIgMCAwIDAgMCA0eiIvPjwvc3ZnPg==",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                className: "center-marker non-interactive-marker",
            });

            centerMarker.current = L.marker(center, {
                icon: redIcon,
                interactive: false,
                keyboard: false,
                zIndexOffset: 1000,
            }).addTo(map);

            setTimeout(makeMarkerNonInteractive, 0);
        },
        [map, makeMarkerNonInteractive]
    );
    const handleFeatureGroupToWorkingGeometry = useCallback(
        (featureGroup: React.MutableRefObject<L.FeatureGroup>) => {
            const result = featureGroupToWorkingGeometry(featureGroup);

            if (featureGroup.current.getLayers().length === 0 && readonlyLayerGroup.current.getLayers().length === 0 && currentCenter && map) {
                updateCenterMarker(currentCenter);
            } else if (centerMarker.current && map) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }

            return result;
        },
        [featureGroupToWorkingGeometry, currentCenter, map, updateCenterMarker]
    );

    const layerOnClick = useCallback(
        (e: L.LeafletMouseEvent, layer: L.Polygon<any> | Polyline<LineString | MultiLineString, any> | Layer) => {
            L.DomEvent.stopPropagation(e.originalEvent);
            L.DomEvent.preventDefault(e.originalEvent);
            if (drawModeRef.current !== GAIA_DRAW_MODE.DELETE || !map) {
                return;
            }
            featureGroup.current.removeLayer(layer);
            map.removeLayer(layer);
            pushHistory({
                points: [...points.current],
                featureGroup: featureGroup.current,
                drawMode: drawModeRef.current,
                isComplete: true,
            });
            const newWorkingGeometry = handleFeatureGroupToWorkingGeometry(featureGroup);
            if (onChange) {
                onChange(newWorkingGeometry, null);
            }
        },
        [map, pushHistory, handleFeatureGroupToWorkingGeometry, onChange]
    );
    const { handleUndo, handleRedo } = useUndoRedo({ map, featureGroup, points, layerOnClick });
    const processGeoJsonLayer = useCallback(
        (map: L.DrawMap, layer: L.Layer, originalBoundary?: any) => {
            const geoJSONLayer = layer as any;
            if (!geoJSONLayer.feature || !geoJSONLayer.feature.geometry) {
                (layer as any)._originalBoundary = originalBoundary?.coordinates ? originalBoundary.coordinates : undefined;
                (layer as any)._initialBoundary = true;
                featureGroup.current.addLayer(layer);
                layer.on("click", (e: L.LeafletMouseEvent) => {
                    layerOnClick(e, layer);
                });
                layer.addTo(map);
                return;
            }
            const geometryType = geoJSONLayer.feature.geometry.type;

            if (geometryType === "MultiPolygon") {
                const multiPolygon = geoJSONLayer.feature.geometry;
                multiPolygon.coordinates.forEach((polygonCoords: number[][][], idx: number) => {
                    const polygonGeometry: Polygon = {
                        type: "Polygon",
                        coordinates: polygonCoords,
                    };

                    const polygonLayer = L.geoJSON<Polygon>(polygonGeometry, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polygon"),
                        } as any,
                    });

                    polygonLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.coordinates ? originalBoundary.coordinates[idx] : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {
                                console.error("Error on layer:", childLayer);
                            }
                        }
                        (childLayer as any)._originalBoundary = ob;
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else if (geometryType === "MultiLineString") {
                const multiLineString = geoJSONLayer.feature.geometry;
                multiLineString.coordinates.forEach((lineCoords: number[][], idx: number) => {
                    const lineGeometry: LineString = {
                        type: "LineString",
                        coordinates: lineCoords,
                    };

                    const lineLayer = L.geoJSON(lineGeometry as any, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polyline"),
                        } as any,
                    });

                    lineLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.coordinates ? originalBoundary.coordinates[idx] : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {
                                console.error("Error on layer:", childLayer);
                            }
                        }
                        (childLayer as any)._originalBoundary = ob;
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else if (geometryType === "MultiPoint" && pointAsCircleMarker) {
                const multiPoint = geoJSONLayer.feature.geometry;
                multiPoint.coordinates.forEach((pointCoords: number[], idx: number) => {
                    const pointGeometry: GeoJSON.Point = {
                        type: "Point",
                        coordinates: pointCoords as any,
                    };

                    const pointLayer = L.geoJSON(pointGeometry as any, {
                        pointToLayer: (_feat, latlng) =>
                            L.circleMarker(latlng, {
                                ...MapViewHelper.getLayerStyling("polygon"),
                            } as any),
                    });

                    pointLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.coordinates ? originalBoundary.coordinates[idx] : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {
                                console.error("Error on layer:", childLayer);
                            }
                        }
                        (childLayer as any)._originalBoundary = ob;
                        // Mark as an actual feature point so generic cleanups don't remove it
                        (childLayer as any)._gaiaRole = "feature";
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else if (geometryType === "Point" && pointAsCircleMarker) {
                const pointFeature: GeoJSON.Feature = geoJSONLayer.feature as any;

                const featureLayer = L.geoJSON(pointFeature as any, {
                    pointToLayer: (_feat, latlng) =>
                        L.circleMarker(latlng, {
                            ...MapViewHelper.getLayerStyling("polygon"),
                        } as any),
                });

                featureLayer.eachLayer((childLayer) => {
                    let ob = originalBoundary?.coordinates ? originalBoundary.coordinates : undefined;
                    if (!ob) {
                        try {
                            const layerGeoJSON = (childLayer as any).toGeoJSON();
                            const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                            const projected = GeometryProjector.projectTo27700(fc);
                            ob = (projected.features[0].geometry as any).coordinates as any;
                        } catch {
                            console.error("Error on layer:", childLayer);
                        }
                    }
                    (childLayer as any)._originalBoundary = ob;
                    // Mark as an actual feature point so generic cleanups don't remove it
                    (childLayer as any)._gaiaRole = "feature";
                    (layer as any)._initialBoundary = true;
                    featureGroup.current.addLayer(childLayer);
                    childLayer.on("click", (e: L.LeafletMouseEvent) => {
                        layerOnClick(e, childLayer);
                    });
                    childLayer.addTo(map);
                });
            } else if (geometryType === "GeometryCollection") {
                const collection = geoJSONLayer.feature.geometry;
                collection.geometries.forEach((geometry: any, idx: number) => {
                    const singleFeature: Feature = {
                        type: "Feature",
                        properties: { ...geoJSONLayer.feature.properties },
                        geometry: geometry,
                    };

                    const featureLayer = L.geoJSON(singleFeature as any, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polygon"),
                        } as any,
                    });

                    featureLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.geometries?.[idx]?.coordinates ? originalBoundary.geometries[idx].coordinates : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {
                                console.error("Error on layer:", childLayer);
                            }
                        }
                        (childLayer as any)._originalBoundary = ob;
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else {
                const existing = originalBoundary?.coordinates ? originalBoundary.coordinates : undefined;
                let boundary27700 = existing;
                if (!boundary27700) {
                    try {
                        const layerGeoJSON = (layer as any).toGeoJSON();
                        const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                        const projected = GeometryProjector.projectTo27700(fc);
                        boundary27700 = (projected.features[0].geometry as any).coordinates as any;
                    } catch (e) {
                        boundary27700 = undefined;
                    }
                }
                (layer as any)._originalBoundary = boundary27700;
                (layer as any)._initialBoundary = true;
                featureGroup.current.addLayer(layer);
                layer.on("click", (e: L.LeafletMouseEvent) => {
                    layerOnClick(e, layer);
                });
                layer.addTo(map);
            }
        },
        [layerOnClick]
    );
    const cancelDrawing = useCallback(() => {
        if (!map || drawMode === null) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.EDIT) {
            const snapshot = (featureGroup.current as any)._editSessionSnapshot as GeoJSON.FeatureCollection | undefined;
            if (snapshot && snapshot.type === "FeatureCollection") {
                try {
                    featureGroup.current.eachLayer((l) => {
                        try {
                            map.removeLayer(l);
                        } catch {
                            console.error("Error removing layer from edit session:", l);
                        }
                    });
                    featureGroup.current.clearLayers();

                    const styled = L.geoJSON(snapshot as any, {
                        style: (feat: any) => {
                            const t = feat?.geometry?.type;
                            if (t === "Polygon" || t === "MultiPolygon") {
                                return { ...MapViewHelper.getLayerStyling("polygon") } as any;
                            }
                            return { ...MapViewHelper.getLayerStyling("polyline") } as any;
                        },
                    });
                    styled.eachLayer((layer) => processGeoJsonLayer(map, layer));

                    (featureGroup.current as any)._editSessionSnapshot = undefined;
                } catch (e) {
                    console.error("Error restoring edit snapshot, falling back to per-layer revert:", e);
                }
            }

            featureGroup.current.eachLayer((l: any) => {
                const leafletLayer = l as L.Polygon | L.Polyline;
                const ob = (leafletLayer as any)._originalBoundary;
                if (!ob) {
                    return;
                }
                try {
                    const isPolygon = leafletLayer instanceof L.Polygon;
                    const geometry: GeoJSON.Geometry = isPolygon
                        ? ({ type: "Polygon", coordinates: Array.isArray(ob[0][0]) ? ob : [ob] } as any)
                        : ({ type: "LineString", coordinates: ob } as any);
                    const wgsGeometry = GeometryProjector.projectToWGS(geometry, "EPSG:27700") as GeoJSON.Geometry;

                    if (wgsGeometry.type === "Polygon") {
                        const ring = (wgsGeometry as GeoJSON.Polygon).coordinates[0] || [];
                        const vertices = ring.map((c) => L.latLng(c[1] as number, c[0] as number));
                        if (vertices.length > 0) {
                            (leafletLayer as L.Polygon).setLatLngs([...vertices, vertices[0]] as any);
                        }
                    } else if (wgsGeometry.type === "LineString") {
                        const line = (wgsGeometry as GeoJSON.LineString).coordinates;
                        const vertices = line.map((c) => L.latLng(c[1] as number, c[0] as number));
                        (leafletLayer as L.Polyline).setLatLngs(vertices as any);
                    }

                    (leafletLayer as any)._hasPendingEdit = false;
                } catch (e) {
                    console.error("Error reverting edited geometry:", e);
                }
            });
        }

        map.eachLayer((layer: any) => {
            if (layer instanceof L.CircleMarker && (layer as any)._gaiaRole === "editing") {
                map.removeLayer(layer);
                return;
            }

            const isTempDrawing = layer.options?.className?.includes("temp-drawing");
            if (isTempDrawing && !isReadonlyLayer(layer)) {
                map.removeLayer(layer);
            }
        });

        points.current = [];
        setDrawMode(null);
    }, [map, drawMode, processGeoJsonLayer, isReadonlyLayer]);
    const onMapRequestHandler = useCallback(async () => {
        if (!onMapBoxSizeRequest) {
            return null;
        }
        const size = await onMapBoxSizeRequest();
        if ("errorMsg" in size && size.errorMsg && size.errorMsg.length > 0) {
            showAlert("Error requesting map box size: " + size.errorMsg, "error");
            return null;
        } else {
            setMapBoxSize(size);
            return size;
        }
    }, [onMapBoxSizeRequest, showAlert]);

    useEffect(() => {
        if (!readOnly) {
            return;
        }

        if (!map) {
            return;
        }

        if (tempCircle.current) {
            map.removeLayer(tempCircle.current);
            tempCircle.current = null;
        }

        const result = setGeometryFromGeoJSON(geometryJson);

        if (result.success && center && !result.didFitBounds) {
            map.setView(center, map.getZoom());
        }

        points.current = [];
        cancelDrawing();
        setDrawMode(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readOnly]);

    useEffect(() => {
        const checkGeometryInterval = setInterval(() => {
            const hasAnyGeometry =
                points.current.length > 0 || !!tempCircle.current || featureGroup.current.getLayers().length > 0 || readonlyLayerGroup.current.getLayers().length > 0;
            setHasGeometry(hasAnyGeometry);

            if (hasAnyGeometry && centerMarker.current && map) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            } else if (!hasAnyGeometry && currentCenter && map && !centerMarker.current) {
                updateCenterMarker(currentCenter);
            }
        }, 100);

        return () => clearInterval(checkGeometryInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        drawModeRef.current = drawMode;
        if (drawMode !== null && mapExists) {
            map.getContainer().style.cursor = "initial";
        }

        if (mapContainer.current) {
            mapContainer.current.setAttribute("data-draw-mode", drawMode || "none");
            if (drawMode === GAIA_DRAW_MODE.CLONE) {
                mapContainer.current.setAttribute("data-mode", "clone");
            } else {
                mapContainer.current.removeAttribute("data-mode");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawMode, mapExists]);

    const acceptDrawing = useCallback(() => {
        if (!mapExists || drawMode === null) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.EDIT) {
            try {
                featureGroup.current.eachLayer((l: any) => {
                    const leafletLayer = l as L.Polygon | L.Polyline;
                    if (!(leafletLayer instanceof L.Polygon || leafletLayer instanceof L.Polyline)) {
                        return;
                    }
                    try {
                        const layerGeoJSON = (leafletLayer as any).toGeoJSON();
                        const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                        const projected = GeometryProjector.projectTo27700(fc);
                        (leafletLayer as any)._originalBoundary = (projected.features[0].geometry as any).coordinates;
                        (leafletLayer as any)._hasPendingEdit = false;
                    } catch (e) {
                        console.error("Error committing edited geometry:", e);
                    }
                });

                try {
                    const toProcess: L.Polygon[] = [];
                    featureGroup.current.eachLayer((ly: any) => {
                        if (ly instanceof L.Polygon) toProcess.push(ly);
                    });
                    toProcess.forEach((pl) => {
                        try {
                            const gj: GeoJSON.Feature<GeoJSON.Polygon> = (pl as any).toGeoJSON();
                            const unk: any = turfUnkink(gj as any);
                            if (unk && (unk as any).features && (unk as any).features.length > 0) {
                                const fc277 = GeometryProjector.projectTo27700(unk as any);
                                let acc: any = null;
                                for (const f of fc277.features as any) {
                                    acc = acc
                                        ? (function () {
                                              try {
                                                  return turfSymDiff(acc as any, f as any) as any;
                                              } catch {
                                                  return turfUnion(acc as any, f as any) as any;
                                              }
                                          })()
                                        : f;
                                }
                                const resFc277: GeoJSON.FeatureCollection =
                                    acc && (acc as any).type === "FeatureCollection" ? (acc as any) : ({ type: "FeatureCollection", features: [acc as any] } as any);
                                const resWgs = GeometryProjector.projectToWGS(resFc277);

                                featureGroup.current.removeLayer(pl);
                                if (map.hasLayer(pl)) map.removeLayer(pl);

                                const splitLayer = L.geoJSON(resWgs as any, {
                                    style: {
                                        ...MapViewHelper.getLayerStyling("polygon"),
                                        className: "accepted-polygon",
                                    } as any,
                                });
                                splitLayer.eachLayer((layer) => processGeoJsonLayer(map, layer));
                            }
                        } catch (e) {
                            console.warn("Self-intersection split/XOR attempt failed:", e);
                        }
                    });
                } catch {
                    console.error("Error splitting edited geometry");
                }

                const newWorkingGeometry = handleFeatureGroupToWorkingGeometry(featureGroup);
                if (onChange) {
                    onChange(newWorkingGeometry, null);
                }
            } finally {
                (featureGroup.current as any)._editSessionSnapshot = undefined;
                setDrawMode(null);
            }
            return;
        }

        if (points.current.length < 3 && (drawMode === GAIA_DRAW_MODE.POLYGON || drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT)) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.CIRCLE) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.RULER) {
            return;
        }

        const coordinates = points.current.map((point) => [point.lng, point.lat]);
        const isPolygonMode = drawMode === GAIA_DRAW_MODE.POLYGON || drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT;

        let geoJsonData: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> | GeoJSON.FeatureCollection = {
            type: "Feature",
            properties: {},
            geometry: {
                type: isPolygonMode ? ("Polygon" as const) : ("LineString" as const as any),
                coordinates: isPolygonMode ? [coordinates] : (coordinates as any),
            } as any,
        } as any;

        if (isPolygonMode && points.current.length >= 3) {
            try {
                let polyPts = [...points.current];
                if (polyPts.length > 0) {
                    const seqClean: L.LatLng[] = [];
                    for (let i = 0; i < polyPts.length; i++) {
                        const cur = polyPts[i];
                        const prev = i > 0 ? polyPts[i - 1] : null;
                        if (!prev || !(prev.lat === cur.lat && prev.lng === cur.lng)) {
                            seqClean.push(cur);
                        }
                    }
                    const seen = new Set<string>();
                    const dedup: L.LatLng[] = [];
                    for (const p of seqClean) {
                        const key = `${p.lat}|${p.lng}`;
                        if (!seen.has(key)) {
                            seen.add(key);
                            dedup.push(p);
                        }
                    }
                    polyPts = dedup;
                }
                if (polyPts.length >= 3) {
                    const first = polyPts[0];
                    const last = polyPts[polyPts.length - 1];
                    if (first.lat !== last.lat || first.lng !== last.lng) {
                        polyPts.push(first);
                    }
                    const coords = polyPts.map((p) => [p.lng, p.lat]);
                    const closed =
                        coords.length > 0 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1] ? coords : [...coords, coords[0]];
                    const gj: GeoJSON.Feature<GeoJSON.Polygon> = {
                        type: "Feature",
                        properties: {},
                        geometry: { type: "Polygon", coordinates: [closed] },
                    } as any;
                    const unk: any = turfUnkink(gj as any);

                    if (unk && Array.isArray((unk as any).features) && (unk as any).features.length > 0) {
                        geoJsonData = unk as GeoJSON.FeatureCollection;
                    } else if (unk && (unk as any).type === "Feature") {
                        geoJsonData = unk as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
                    } else {
                        geoJsonData = gj as any;
                    }
                }
            } catch (e) {
                console.error("Error creating polygon from points:", e);
            }
        }

        if (drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT) {
            if (onSelect) {
                const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {
                    type: "FeatureCollection",
                    features: [geoJsonData as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>],
                };
                const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                onSelect(reprojectedGeometry.features[0] as GeoJSON.Feature<GeoJSON.Polygon>, drawMode === GAIA_DRAW_MODE.SELECT ? "select" : "deselect");
            }
        } else {
            const dataToAdd: any = (geoJsonData as any).type === "FeatureCollection" ? (geoJsonData as any) : (geoJsonData as any);
            const geoJsonLayer = L.geoJSON(dataToAdd, {
                style: {
                    ...MapViewHelper.getLayerStyling(drawMode === GAIA_DRAW_MODE.POLYGON ? "polygon" : "polyline"),
                    className: drawMode === GAIA_DRAW_MODE.POLYGON ? "accepted-polygon" : "",
                } as any,
            });

            const getCoordsFromLatLngs = (latLngs: L.LatLng[] | L.LatLng[][] | L.LatLng[][][]): [number, number][][] => {
                if (!latLngs || !(latLngs as any).length) return [] as any;
                if (Array.isArray(latLngs[0]) && (latLngs[0] as any)[0] instanceof L.LatLng) {
                    return (latLngs as L.LatLng[][]).map((ring) => ring.map((ll) => [ll.lng, ll.lat] as [number, number]));
                } else if (latLngs[0] instanceof L.LatLng) {
                    return [(latLngs as L.LatLng[]).map((ll) => [ll.lng, ll.lat] as [number, number])];
                } else if (Array.isArray(latLngs[0]) && Array.isArray((latLngs as any)[0][0]) && (latLngs as any)[0][0][0] instanceof L.LatLng) {
                    return ((latLngs as any)[0] as L.LatLng[][]).map((ring) => ring.map((ll) => [ll.lng, ll.lat] as [number, number]));
                }
                return [] as any;
            };

            const unionWithExisting = (newPolyLayer: L.Polygon): void => {
                const newCoordsArrays = getCoordsFromLatLngs(newPolyLayer.getLatLngs() as any);
                if (!newCoordsArrays.length || newCoordsArrays[0].length < 3) {
                    processGeoJsonLayer(map, newPolyLayer as any);
                    return;
                }
                for (const ring of newCoordsArrays) {
                    const first = ring[0];
                    const last = ring[ring.length - 1];
                    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
                        ring.push([first[0], first[1]]);
                    }
                }

                let merged: any;
                try {
                    const newFcWgs: GeoJSON.FeatureCollection = {
                        type: "FeatureCollection",
                        features: [
                            {
                                type: "Feature",
                                properties: {},
                                geometry: { type: "Polygon", coordinates: newCoordsArrays } as any,
                            },
                        ],
                    } as any;
                    const newFc277 = GeometryProjector.projectTo27700(newFcWgs);
                    const projectedNewCoords = (newFc277.features[0].geometry as GeoJSON.Polygon).coordinates as any;
                    merged = turfPolygon(projectedNewCoords as any);
                } catch (e) {
                    processGeoJsonLayer(map, newPolyLayer as any);
                    return;
                }

                const overlapping: L.Polygon[] = [];
                featureGroup.current.eachLayer((l: any) => {
                    if (!(l instanceof L.Polygon)) return;
                    try {
                        const latlngs = (l as L.Polygon).getLatLngs();
                        const coordsArrays = getCoordsFromLatLngs(latlngs as any);
                        if (!coordsArrays.length) return;
                        for (const ring of coordsArrays) {
                            const f = ring[0];
                            const la = ring[ring.length - 1];
                            if (f && la && (f[0] !== la[0] || f[1] !== la[1])) ring.push([f[0], f[1]]);
                        }
                        let feat: any;
                        const originalBoundary = (l as any)._originalBoundary;
                        if (originalBoundary) {
                            feat = turfPolygon(originalBoundary as any);
                        } else {
                            const existFcWgs: GeoJSON.FeatureCollection = {
                                type: "FeatureCollection",
                                features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: coordsArrays } as any }],
                            } as any;
                            const existFc277 = GeometryProjector.projectTo27700(existFcWgs);
                            const projExistCoords = (existFc277.features[0].geometry as GeoJSON.Polygon).coordinates as any;
                            feat = turfPolygon(projExistCoords as any);
                        }
                        if (turfIntersect(feat as any, merged as any)) {
                            try {
                                merged = turfUnion(merged as any, feat as any) as any;
                                overlapping.push(l as L.Polygon);
                            } catch (error) {
                                console.error("Error processing polygon union:", error);
                            }
                        }
                    } catch (error) {
                        console.error("Error processing polygon union:", error);
                    }
                });

                overlapping.forEach((l) => {
                    featureGroup.current.removeLayer(l);
                    if (map.hasLayer(l)) map.removeLayer(l);
                });
                if (map.hasLayer(newPolyLayer)) map.removeLayer(newPolyLayer);

                const merged277 = merged.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
                const resultFc277: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: merged277 as any }] } as any;
                const resultWgs = GeometryProjector.projectToWGS(resultFc277);
                const styled = L.geoJSON(resultWgs as any, {
                    style: { ...MapViewHelper.getLayerStyling("polygon"), className: "accepted-polygon" } as any,
                });
                styled.eachLayer((child) => processGeoJsonLayer(map, child, merged277 as any));
            };

            const hasExistingPolys = featureGroup.current.getLayers().some((ly) => ly instanceof L.Polygon);
            if (drawMode === GAIA_DRAW_MODE.POLYGON && hasExistingPolys && !singleGeometryMode) {
                geoJsonLayer.eachLayer((layer) => {
                    if (layer instanceof L.Polygon) {
                        unionWithExisting(layer);
                    } else {
                        processGeoJsonLayer(map, layer);
                    }
                });
            } else {
                geoJsonLayer.eachLayer((layer) => processGeoJsonLayer(map, layer));
            }
        }

        map.eachLayer((layer: any) => {
            const tempDrawing = layer.options?.className?.includes("temp-drawing");
            if (tempDrawing && !isReadonlyLayer(layer)) {
                map.removeLayer(layer);
            }
            if (layer instanceof L.CircleMarker && (layer as any)._gaiaRole === "editing") {
                map.removeLayer(layer);
            }
        });

        pushHistory(
            {
                points: [],
                featureGroup: featureGroup.current,
                drawMode: null,
                isComplete: drawMode === GAIA_DRAW_MODE.POLYGON,
            },
            false
        );

        if (drawMode !== GAIA_DRAW_MODE.SELECT && drawMode !== GAIA_DRAW_MODE.DESELECT) {
            const newWorkingGeometry = handleFeatureGroupToWorkingGeometry(featureGroup);
            if (onChange) {
                suppressNextFitRef.current = true;
                const lastFeature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> =
                    (geoJsonData as any).type === "FeatureCollection"
                        ? (geoJsonData as GeoJSON.FeatureCollection).features[0]
                        : (geoJsonData as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>);
                const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [lastFeature] };
                const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                onChange(newWorkingGeometry, JSON.stringify(reprojectedGeometry.features[0]));
            }
        }

        points.current = [];
        setDrawMode(null);
    }, [mapExists, drawMode, map, pushHistory, featureGroupToWorkingGeometry, onChange, processGeoJsonLayer, onSelect]);

    const centerMap = useCallback(
        (newCenter: L.LatLngExpression | null) => {
            if (!mapExists) {
                return;
            }
            if (newCenter) {
                map.setView(newCenter, map.getZoom());
                setCurrentCenter(newCenter);
            } else if (featureGroup.current.getLayers().length > 0) {
                try {
                    const bounds = featureGroup.current.getBounds();
                    if (bounds.isValid()) {
                        map.fitBounds(bounds, { padding: [20, 20] });
                    }
                } catch (error) {
                    showAlert("Error centering map on geometry", "error");
                    console.error("Error centering map on geometry:", error);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mapExists]
    );

    const suppressNextFitRef = useRef(false);
    const hasInitialGeometryLoadedRef = useRef(false);
    const hasInitialReadonlyLayersLoadedRef = useRef(false);

    const suppressNextFit = useCallback(() => {
        suppressNextFitRef.current = true;
    }, []);

    const setGeometryFromGeoJSON = useCallback(
        (geoJsonString: string | null | undefined, options?: { fitBounds?: boolean }): { success: boolean; didFitBounds?: boolean } => {
            const fitBounds = options?.fitBounds ?? true;
            if (!mapExists) {
                return { success: false };
            }

            if (centerMarker.current) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }

            if (!geoJsonString) {
                featureGroup.current.eachLayer((layer) => map.removeLayer(layer));
                featureGroup.current.clearLayers();
                updateWorkingGeometry(geoJsonString || "");
                pushHistory(
                    {
                        points: [],
                        featureGroup: featureGroup.current,
                        drawMode: null,
                        isComplete: true,
                    },
                    false,
                    true
                );

                if (currentCenter && readonlyLayerGroup.current.getLayers().length === 0) {
                    updateCenterMarker(currentCenter);
                }

                return { success: true, didFitBounds: false };
            }

            try {
                const geoJsonData = JSON.parse(geoJsonString);
                const projected = GeometryProjector.projectToWGS(geoJsonData);
                const geoJsonLayer = L.geoJSON(projected, {
                    style: {
                        ...MapViewHelper.getLayerStyling("polygon"),
                    } as any,
                });
                featureGroup.current.eachLayer((layer) => map.removeLayer(layer));
                featureGroup.current.clearLayers();

                geoJsonLayer.eachLayer((layer) => processGeoJsonLayer(map, layer, geoJsonData));

                featureGroupToWorkingGeometry(featureGroup);

                let didFitBounds = false;
                try {
                    const bounds = featureGroup.current.getBounds();
                    if (fitBounds && bounds.isValid()) {
                        if (readonlyLayerGroup.current.getLayers().length > 0) {
                            try {
                                let readonlyBounds: L.LatLngBounds | null = null;
                                readonlyLayerGroup.current.eachLayer((layer) => {
                                    if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
                                        const layerBounds = L.latLngBounds([layer.getLatLng()]);
                                        readonlyBounds = readonlyBounds ? readonlyBounds.extend(layerBounds) : layerBounds;
                                    } else if (layer instanceof L.Polygon || layer instanceof L.Polyline || layer instanceof L.Circle) {
                                        const layerBounds = layer.getBounds();
                                        readonlyBounds = readonlyBounds ? readonlyBounds.extend(layerBounds) : layerBounds;
                                    } else if ((layer as any).getBounds) {
                                        try {
                                            const layerBounds = (layer as any).getBounds() as L.LatLngBounds | undefined;
                                            if (layerBounds && layerBounds.isValid && layerBounds.isValid()) {
                                                readonlyBounds = readonlyBounds ? readonlyBounds.extend(layerBounds) : layerBounds;
                                            }
                                        } catch {
                                            // Ignore layers without getBounds
                                        }
                                    }
                                });

                                if (readonlyBounds && typeof (readonlyBounds as any).isValid === "function" && (readonlyBounds as L.LatLngBounds).isValid()) {
                                    const geometryArea = bounds.getNorthEast().distanceTo(bounds.getSouthWest());
                                    const readonlyArea = (readonlyBounds as L.LatLngBounds).getNorthEast().distanceTo((readonlyBounds as L.LatLngBounds).getSouthWest());

                                    // If readonlyLayers have significantly larger area (e.g., polygon vs point),
                                    // use readonlyLayers as base and extend with geometry
                                    // Otherwise, combine normally
                                    if (readonlyArea > geometryArea * 10) {
                                        // readonlyLayers are much larger, use them as base
                                        const combinedBounds = (readonlyBounds as L.LatLngBounds).extend(bounds);
                                        map.fitBounds(combinedBounds, { padding: [20, 20] });
                                    } else {
                                        // Similar sizes, combine normally
                                        const combinedBounds = bounds.extend(readonlyBounds as L.LatLngBounds);
                                        map.fitBounds(combinedBounds, { padding: [20, 20] });
                                    }
                                } else {
                                    map.fitBounds(bounds, { padding: [20, 20] });
                                }
                            } catch (error) {
                                // If combining fails, just fit to main geometry
                                map.fitBounds(bounds, { padding: [20, 20] });
                            }
                        } else {
                            map.fitBounds(bounds, { padding: [20, 20] });
                        }
                        didFitBounds = true;
                    }
                } catch (error) {
                    showAlert("Error centering map on imported geometry", "error");
                    console.error("Error centering map on imported geometry:", error);
                }

                pushHistory(
                    {
                        points: [],
                        featureGroup: featureGroup.current,
                        drawMode: null,
                        isComplete: true,
                    },
                    false,
                    true
                );

                return { success: true, didFitBounds };
            } catch (error) {
                showAlert("Error setting geometry from GeoJSON", "error");
                console.error("Error setting geometry from GeoJSON:", error);

                return { success: false };
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layerOnClick, mapExists, pushHistory, updateWorkingGeometry]
    );

    useEffect(() => {
        if (!mapExists || geometryJson === workingGeometry) {
            return;
        }

        const isInitialLoad = !hasInitialGeometryLoadedRef.current && !!geometryJson;
        const shouldFitBounds = Boolean(isInitialLoad || !suppressNextFitRef.current);

        const result = setGeometryFromGeoJSON(geometryJson, { fitBounds: shouldFitBounds });
        suppressNextFitRef.current = false;

        if (result.success) {
            if (isInitialLoad) {
                hasInitialGeometryLoadedRef.current = true;
            }

            if (center && (!result.didFitBounds || !geometryJson)) {
                map.setView(center, map.getZoom());
            }

            if (!geometryJson && readonlyLayerGroup.current.getLayers().length === 0 && center) {
                updateCenterMarker(center);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapExists, geometryJson]);

    useEffect(() => {
        if (!mapExists) {
            return;
        }
        if (center) {
            map.setView(center, map.getZoom());

            if (featureGroup.current.getLayers().length === 0 && readonlyLayerGroup.current.getLayers().length === 0) {
                updateCenterMarker(center);
            } else if (centerMarker.current) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }
        } else if (featureGroup.current.getLayers().length > 0) {
            try {
                const bounds = featureGroup.current.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] });
                }
            } catch (error) {
                console.error("Error centering map on geometry:", error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapExists, center]);

    const { theme } = useGaiaMapTheme();

    const onLegendCheckboxClicked = useCallback((e: MirageOnChangeEvent, value: any, layer: MapWmsLayer): void => {
        e.stopPropagation();
        setWmsLayers((prev) => {
            const newWmsLayers = [...prev];
            const index = newWmsLayers.findIndex((l) => l.layer.name === layer.layer.name);
            if (index !== -1) {
                newWmsLayers[index].selected = value;
            }
            return newWmsLayers;
        });
    }, []);
    const getWmsLegendItems = useCallback(
        (layers: MapWmsLayer[]) => {
            return layers.map((r) => {
                return (
                    <Field
                        containerClassName="mapView__legendField"
                        key={r.layer.name}
                        value={r.selected}
                        contentType="CHECKBOX"
                        label={r.layer.title}
                        onChange={(e, v) => onLegendCheckboxClicked(e, v, r)}
                        mode={"edit"}
                    />
                );
            });
        },
        [onLegendCheckboxClicked]
    );
    const wmsLayerFields: JSX.Element[] = wms ? (getWmsLegendItems(wmsLayers).filter((f) => f !== null) as JSX.Element[]) : [];
    const legend =
        wms && wms.showLegend ? <Legend map={map} header={wmsLegendHeader ? <Typography variant="h6">{wmsLegendHeader}</Typography> : null} rows={wmsLayerFields} /> : null;

    useEffect(() => {
        if (!wms) {
            return;
        }
        const showSelectedWmsLayers = () => {
            const layersArray = wmsLayers.filter((l) => l.selected).map((l) => l.layer.name);
            const layersString = layersArray.join(",");
            if (!map || !wms?.url) {
                return layersArray;
            }
            if (wmsLayer) {
                wmsLayer.options.layers = layersString;
                wmsLayer.initialize(wms.url, wmsLayer.options);
                wmsLayer.redraw();
            } else {
                const nonTitled = (L as any).nonTiledLayer as any;
                const newWmsLayer = new nonTitled.WMS(wms.url as string, {
                    layers: layersString,
                    opacity: 1.0,
                    format: "image/png",
                    transparent: true,
                    zIndex: 5,
                    attribution: "",
                });
                newWmsLayer.addTo(map);
                setWmsLayer(newWmsLayer);
            }
            return layersArray;
        };
        const lrs = showSelectedWmsLayers();
        wms.onShownLayersChanged?.(lrs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWmsLayersCount]);

    useEffect(() => {
        if (!map) {
            return;
        }
        const roGroup = readonlyLayerGroup.current;
        const panesMap = readonlyLayerPanes.current;
        roGroup.addTo(map);
        return () => {
            if (map.hasLayer(roGroup)) {
                map.removeLayer(roGroup);
            }
            // Clean up custom panes
            panesMap.forEach((pane) => {
                if (pane && pane.parentNode) {
                    pane.parentNode.removeChild(pane);
                }
            });
            panesMap.clear();
        };
    }, [map]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const readonlyLayerGroupRef = readonlyLayerGroup.current;
        const currentLabelMarkersRef = labelMarkersRef.current;

        const sessionState = new Map<number, { pos: L.LatLng; dragged: boolean }>();
        currentLabelMarkersRef.forEach((marker, id) => {
            if ((marker as any)._gaiaRole !== "editing") {
                sessionState.set(id, { pos: marker.getLatLng(), dragged: (marker as any)._isDraggedInSession === true });
            }
        });

        readonlyLayerGroupRef.eachLayer((layer) => {
            const popupRoot = (layer as any)._popupRoot;
            if (popupRoot) {
                setTimeout(() => {
                    popupRoot.unmount();
                }, 0);
            }
            const dragCleanup = (layer as any)._labelDragCleanup;
            if (dragCleanup) {
                dragCleanup();
            }
        });

        readonlyLayerGroupRef.clearLayers();
        sessionState.forEach((_, id) => currentLabelMarkersRef.delete(id));

        (readonlyLayers || []).forEach((readonlyLayer) => {
            if (!readonlyLayer?.geometryJson) {
                return;
            }

            try {
                const parsed = JSON.parse(readonlyLayer.geometryJson);
                const projected = GeometryProjector.projectToWGS(parsed);
                const dashArray = geometryOutlinePatternToDashArray(readonlyLayer.outlinePattern);
                const basePolygonStyle = MapViewHelper.getLayerStyling("polygon") as L.PathOptions;
                const basePolylineStyle = MapViewHelper.getLayerStyling("polyline") as L.PathOptions;

                // Create custom pane for this layer if zIndexOffset is provided
                let customPaneName: string | undefined;
                if (readonlyLayer.zIndexOffset !== undefined && map) {
                    customPaneName = `readonly-pane-${readonlyLayer.zIndexOffset}`;
                    let pane = readonlyLayerPanes.current.get(readonlyLayer.zIndexOffset);

                    if (!pane) {
                        if (!map.getPane(customPaneName)) {
                            pane = map.createPane(customPaneName);
                            // Base z-index for overlay pane is 400, add offset
                            const baseZIndex = 400;
                            pane.style.zIndex = (baseZIndex + readonlyLayer.zIndexOffset).toString();
                            readonlyLayerPanes.current.set(readonlyLayer.zIndexOffset, pane);
                        } else {
                            pane = map.getPane(customPaneName) as HTMLElement;
                            readonlyLayerPanes.current.set(readonlyLayer.zIndexOffset, pane);
                        }
                    }
                }

                const geoJsonLayer = L.geoJSON(projected as any, {
                    style: (feature?: GeoJSON.Feature<GeoJSON.Geometry>) => {
                        const geometryType = feature?.geometry?.type;
                        const isPolygonLike = geometryType?.includes("Polygon");
                        const baseStyle = isPolygonLike ? basePolygonStyle : basePolylineStyle;
                        const overrideStyle: L.PathOptions = { ...(baseStyle as L.PathOptions) };
                        overrideStyle.className = "readonly-layer";
                        if (readonlyLayer.outlineColor) {
                            overrideStyle.color = readonlyLayer.outlineColor;
                        }
                        if (readonlyLayer.fillColor && isPolygonLike) {
                            overrideStyle.fillColor = readonlyLayer.fillColor;
                        }
                        if (dashArray) {
                            overrideStyle.dashArray = dashArray;
                        }
                        // Set pane if custom pane was created
                        if (customPaneName) {
                            (overrideStyle as any).pane = customPaneName;
                        }
                        return overrideStyle;
                    },
                    pointToLayer: (_feat, latlng) => {
                        const markerOptions: any = {
                            ...basePolygonStyle,
                            className: "readonly-layer",
                            color: readonlyLayer.outlineColor || basePolygonStyle.color,
                            fillColor: readonlyLayer.fillColor || basePolygonStyle.fillColor,
                            dashArray,
                        };
                        if (readonlyLayer.zIndexOffset !== undefined) {
                            markerOptions.zIndexOffset = readonlyLayer.zIndexOffset;
                        }
                        // For CircleMarker, also set pane if custom pane exists
                        if (customPaneName) {
                            markerOptions.pane = customPaneName;
                        }
                        return L.circleMarker(latlng, markerOptions);
                    },
                    onEachFeature: (_feature, layer) => {
                        (layer as any)._gaiaRole = "readonly";
                        (layer as any)._readonly = true;

                        // Apply zIndexOffset if provided (pane already set during creation for Path layers)
                        if (readonlyLayer.zIndexOffset !== undefined) {
                            if (layer instanceof L.Marker) {
                                // For Marker, use the setZIndexOffset method
                                layer.setZIndexOffset(readonlyLayer.zIndexOffset);
                            }
                            // Store in options for reference
                            (layer.options as any).zIndexOffset = readonlyLayer.zIndexOffset;
                        }

                        setTimeout(() => {
                            const el = (layer as any).getElement?.();
                            if (el) {
                                el.classList.add("readonly-layer");
                            }
                        }, 0);

                        // Only enable popups and interactivity when readOnly is true
                        // When in edit mode, layers should be non-interactive to not interfere with drawing
                        if (readOnly) {
                            if (layer instanceof L.Path) {
                                layer.options.interactive = true;
                            }

                            // Bind popup if popupContent is provided and map is readonly
                            if (readonlyLayer.popupContent) {
                                const popupContainer = document.createElement("div");
                                const popupRoot = ReactDOM.createRoot(popupContainer);
                                popupRoot.render(<>{readonlyLayer.popupContent}</>);

                                layer.bindPopup(popupContainer, {
                                    autoPan: true,
                                    closeButton: true,
                                });

                                // Make layer interactive if it isn't already
                                if (layer instanceof L.Path) {
                                    layer.options.interactive = true;
                                } else if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
                                    layer.options.interactive = true;
                                }

                                // Store popup root for cleanup
                                (layer as any)._popupRoot = popupRoot;
                            }
                        } else {
                            // In edit mode, make layers non-interactive to prevent interference with drawing
                            if (layer instanceof L.Path) {
                                layer.options.interactive = false;
                            } else if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
                                layer.options.interactive = false;
                            }
                            // Unbind any existing popups
                            if (layer.getPopup()) {
                                layer.unbindPopup();
                            }
                        }
                    },
                });

                geoJsonLayer.addTo(readonlyLayerGroup.current);

                if (readonlyLayer.label?.text) {
                    let labelPosition: L.LatLng;
                    const hadOriginalPosition = !!readonlyLayer.label.position;

                    const sState = sessionState.get(readonlyLayer.id);
                    const wasDraggedInSession = sState ? sState.dragged : false;
                    const currentMarkerPosition = sState ? sState.pos : null;

                    if (readonlyLayer.label.position) {
                        const wgs84Coords = GeometryProjector.projCoordinatesToWGS84(readonlyLayer.label.position);
                        labelPosition = L.latLng(wgs84Coords[1], wgs84Coords[0]);
                    } else if (wasDraggedInSession && currentMarkerPosition) {
                        labelPosition = currentMarkerPosition;
                    } else {
                        // If no position provided, center on geometry
                        const bounds = geoJsonLayer.getBounds();
                        if (bounds.isValid()) {
                            labelPosition = bounds.getCenter();
                        } else {
                            console.error("Cannot calculate geometry center for label - invalid bounds");
                            return;
                        }
                    }

                    const invisibleIcon = L.divIcon({
                        className: "gaia-label-marker",
                        iconSize: [0, 0],
                        iconAnchor: [0, 0],
                        html: "",
                    });

                    const labelMarker = L.marker(labelPosition, {
                        icon: invisibleIcon,
                        interactive: false,
                        draggable: false,
                        keyboard: false,
                        zIndexOffset: readonlyLayer.zIndexOffset !== undefined ? readonlyLayer.zIndexOffset + 100 : 1000,
                    });

                    (labelMarker as any)._labelLayerId = readonlyLayer.id;
                    (labelMarker as any)._labelText = readonlyLayer.label.text;
                    (labelMarker as any)._hadOriginalPosition = hadOriginalPosition;
                    (labelMarker as any)._isDraggedInSession = wasDraggedInSession;
                    (labelMarker as any)._gaiaRole = "readonly";

                    const currentText = readonlyLayer.label.text;

                    const createLabelContent = (text: string): HTMLSpanElement => {
                        const labelElement = document.createElement("span");
                        labelElement.className = "gaia-label-text";
                        labelElement.textContent = text;
                        return labelElement;
                    };

                    const initialContent = createLabelContent(currentText);

                    labelMarker.bindTooltip(initialContent, {
                        permanent: true,
                        direction: "center",
                        className: "gaia-label-tooltip",
                        offset: [0, 0],
                        interactive: false,
                        opacity: 1,
                    });

                    labelMarkersRef.current.set(readonlyLayer.id, labelMarker);

                    if (updateGeometryLabel) {
                        let isDragging = false;
                        let dragStartPos: { x: number; y: number } | null = null;
                        let dragStartLatLng: L.LatLng | null = null;

                        const handleMouseMove = (e: MouseEvent) => {
                            if (isDragging && dragStartPos && dragStartLatLng && map && drawModeRef.current === GAIA_DRAW_MODE.LABELS) {
                                e.preventDefault();
                                e.stopPropagation();

                                const deltaX = e.clientX - dragStartPos.x;
                                const deltaY = e.clientY - dragStartPos.y;

                                const point = map.latLngToContainerPoint(dragStartLatLng);
                                const newPoint = L.point(point.x + deltaX, point.y + deltaY);
                                const newLatLng = map.containerPointToLatLng(newPoint);

                                labelMarker.setLatLng(newLatLng);
                                (labelMarker as any)._isDraggedInSession = true;
                            }
                        };

                        const handleMouseUp = () => {
                            if (isDragging) {
                                isDragging = false;

                                if (map) {
                                    map.dragging.enable();
                                }

                                document.body.style.cursor = "";
                                dragStartPos = null;
                                dragStartLatLng = null;
                            }
                        };

                        document.addEventListener("mousemove", handleMouseMove, true);
                        document.addEventListener("mouseup", handleMouseUp, true);

                        (labelMarker as any)._labelDragCleanup = () => {
                            document.removeEventListener("mousemove", handleMouseMove, true);
                            document.removeEventListener("mouseup", handleMouseUp, true);
                        };

                        const setupLabelDragHandlers = () => {
                            const tooltip = labelMarker.getTooltip();
                            if (tooltip) {
                                const tooltipElement = (tooltip as any)._container as HTMLElement;
                                if (tooltipElement) {
                                    tooltipElement.style.pointerEvents = "auto";

                                    const labelElement = tooltipElement.querySelector(".gaia-label-text") as HTMLElement;
                                    if (labelElement) {
                                        labelElement.style.pointerEvents = "auto";

                                        const handleMouseDown = (e: MouseEvent) => {
                                            if (drawModeRef.current === GAIA_DRAW_MODE.LABELS && e.button === 0) {
                                                e.stopPropagation();
                                                e.stopImmediatePropagation();
                                                e.preventDefault();

                                                isDragging = true;
                                                dragStartPos = { x: e.clientX, y: e.clientY };
                                                dragStartLatLng = labelMarker.getLatLng();

                                                if (map) {
                                                    map.dragging.disable();
                                                }

                                                document.body.style.cursor = "grabbing";
                                            }
                                        };

                                        labelElement.addEventListener("mousedown", handleMouseDown, true);
                                    }
                                }
                            }
                        };

                        (labelMarker as any)._setupLabelDragHandlers = setupLabelDragHandlers;

                        setTimeout(setupLabelDragHandlers, 50);
                    }

                    if (customPaneName && map) {
                        labelMarker.options.pane = customPaneName;
                    }

                    labelMarker.addTo(readonlyLayerGroup.current);
                }
            } catch (error) {
                console.error("Error rendering readonly layer:", error);
            }
        });

        if (readonlyLayerGroup.current.getLayers().length > 0 && centerMarker.current && map) {
            map.removeLayer(centerMarker.current);
            centerMarker.current = null;
        }

        const isInitialReadonlyLayersLoad = !hasInitialReadonlyLayersLoadedRef.current && readonlyLayerGroup.current.getLayers().length > 0;
        if (isInitialReadonlyLayersLoad && map) {
            hasInitialReadonlyLayersLoadedRef.current = true;

            try {
                let readonlyBounds: L.LatLngBounds | null = null;
                readonlyLayerGroup.current.eachLayer((layer) => {
                    if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
                        const layerBounds = L.latLngBounds([layer.getLatLng()]);
                        readonlyBounds = readonlyBounds ? readonlyBounds.extend(layerBounds) : layerBounds;
                    } else if (layer instanceof L.Polygon || layer instanceof L.Polyline || layer instanceof L.Circle) {
                        const layerBounds = layer.getBounds();
                        readonlyBounds = readonlyBounds ? readonlyBounds.extend(layerBounds) : layerBounds;
                    } else if ((layer as any).getBounds) {
                        try {
                            const layerBounds = (layer as any).getBounds() as L.LatLngBounds | undefined;
                            if (layerBounds && layerBounds.isValid && layerBounds.isValid()) {
                                readonlyBounds = readonlyBounds ? readonlyBounds.extend(layerBounds) : layerBounds;
                            }
                        } catch {
                            // Ignore layers without getBounds
                        }
                    }
                });

                if (readonlyBounds && typeof (readonlyBounds as any).isValid === "function" && (readonlyBounds as L.LatLngBounds).isValid()) {
                    if (featureGroup.current.getLayers().length > 0) {
                        try {
                            const geometryBounds = featureGroup.current.getBounds();
                            if (geometryBounds.isValid()) {
                                const geometryArea = geometryBounds.getNorthEast().distanceTo(geometryBounds.getSouthWest());
                                const readonlyArea = (readonlyBounds as L.LatLngBounds).getNorthEast().distanceTo((readonlyBounds as L.LatLngBounds).getSouthWest());

                                // If readonlyLayers have significantly larger area (e.g., polygon vs point),
                                // use readonlyLayers as base and extend with geometry
                                // Otherwise, combine normally
                                if (readonlyArea > geometryArea * 10) {
                                    // readonlyLayers are much larger, use them as base
                                    const combinedBounds = (readonlyBounds as L.LatLngBounds).extend(geometryBounds);
                                    map.fitBounds(combinedBounds, { padding: [20, 20] });
                                } else {
                                    // Similar sizes, combine normally
                                    const combinedBounds = geometryBounds.extend(readonlyBounds as L.LatLngBounds);
                                    map.fitBounds(combinedBounds, { padding: [20, 20] });
                                }
                            } else {
                                map.fitBounds(readonlyBounds as L.LatLngBounds, { padding: [20, 20] });
                            }
                        } catch (error) {
                            map.fitBounds(readonlyBounds as L.LatLngBounds, { padding: [20, 20] });
                        }
                    } else {
                        map.fitBounds(readonlyBounds as L.LatLngBounds, { padding: [20, 20] });
                    }
                }
            } catch (error) {
                console.error("Error fitting bounds to readonly layers:", error);
            }
        }

        return () => {
            readonlyLayerGroupRef.eachLayer((layer) => {
                const popupRoot = (layer as any)._popupRoot;
                if (popupRoot) {
                    setTimeout(() => {
                        popupRoot.unmount();
                    }, 0);
                }
                const dragCleanup = (layer as any)._labelDragCleanup;
                if (dragCleanup) {
                    dragCleanup();
                }
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, readonlyLayers, colorPalette, theme.palette, readOnly, updateGeometryLabel]);

    const editingGeometryLabelMarkerRef = useRef<L.Marker | null>(null);
    useEffect(() => {
        if (!map) {
            return;
        }

        let currentMarkerPosition: L.LatLng | null = null;
        let wasDraggedInSession = false;
        if (editingGeometryLabelMarkerRef.current) {
            const marker = editingGeometryLabelMarkerRef.current;
            const previousRecordId = (marker as any)._labelLayerId;
            if (previousRecordId === geometryLabel?.recordId) {
                currentMarkerPosition = marker.getLatLng();
                wasDraggedInSession = (marker as any)._isDraggedInSession === true;
            }
            const dragCleanup = (marker as any)._labelDragCleanup;
            if (dragCleanup) {
                dragCleanup();
            }
            if (map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
            if (previousRecordId !== undefined) {
                labelMarkersRef.current.delete(previousRecordId);
            }
            editingGeometryLabelMarkerRef.current = null;
        }

        if (geometryLabel?.text && geometryLabel?.recordId !== undefined && featureGroup.current.getLayers().length > 0) {
            try {
                const bounds = featureGroup.current.getBounds();
                if (!bounds.isValid()) {
                    return;
                }

                let labelPosition: L.LatLng;
                const hadOriginalPosition = !!geometryLabel.position;
                if (geometryLabel.position) {
                    const wgs84Coords = GeometryProjector.projCoordinatesToWGS84(geometryLabel.position);
                    labelPosition = L.latLng(wgs84Coords[1], wgs84Coords[0]);
                } else if (wasDraggedInSession && currentMarkerPosition) {
                    labelPosition = currentMarkerPosition;
                } else {
                    labelPosition = bounds.getCenter();
                }

                const invisibleIcon = L.divIcon({
                    className: "gaia-label-marker",
                    iconSize: [0, 0],
                    iconAnchor: [0, 0],
                    html: "",
                });

                const labelMarker = L.marker(labelPosition, {
                    icon: invisibleIcon,
                    interactive: false,
                    draggable: false,
                    keyboard: false,
                    zIndexOffset: 1000,
                });

                (labelMarker as any)._labelLayerId = geometryLabel.recordId;
                (labelMarker as any)._labelText = geometryLabel.text;
                (labelMarker as any)._hadOriginalPosition = hadOriginalPosition;
                (labelMarker as any)._isDraggedInSession = wasDraggedInSession;
                (labelMarker as any)._gaiaRole = "editing";

                const createLabelContent = (text: string): HTMLSpanElement => {
                    const labelElement = document.createElement("span");
                    labelElement.className = "gaia-label-text";
                    labelElement.textContent = text;
                    return labelElement;
                };

                const initialContent = createLabelContent(geometryLabel.text);

                labelMarker.bindTooltip(initialContent, {
                    permanent: true,
                    direction: "center",
                    className: "gaia-label-tooltip",
                    offset: [0, 0],
                    interactive: false,
                    opacity: 1,
                });

                labelMarkersRef.current.set(geometryLabel.recordId, labelMarker);
                editingGeometryLabelMarkerRef.current = labelMarker;

                if (updateGeometryLabel) {
                    let isDragging = false;
                    let dragStartPos: { x: number; y: number } | null = null;
                    let dragStartLatLng: L.LatLng | null = null;

                    const handleMouseMove = (e: MouseEvent) => {
                        if (isDragging && dragStartPos && dragStartLatLng && map && drawModeRef.current === GAIA_DRAW_MODE.LABELS) {
                            e.preventDefault();
                            e.stopPropagation();

                            const deltaX = e.clientX - dragStartPos.x;
                            const deltaY = e.clientY - dragStartPos.y;

                            const point = map.latLngToContainerPoint(dragStartLatLng);
                            const newPoint = L.point(point.x + deltaX, point.y + deltaY);
                            const newLatLng = map.containerPointToLatLng(newPoint);

                            labelMarker.setLatLng(newLatLng);
                            (labelMarker as any)._isDraggedInSession = true;
                        }
                    };

                    const handleMouseUp = () => {
                        if (isDragging) {
                            isDragging = false;

                            if (map) {
                                map.dragging.enable();
                            }

                            document.body.style.cursor = "";
                            dragStartPos = null;
                            dragStartLatLng = null;
                        }
                    };

                    document.addEventListener("mousemove", handleMouseMove, true);
                    document.addEventListener("mouseup", handleMouseUp, true);

                    (labelMarker as any)._labelDragCleanup = () => {
                        document.removeEventListener("mousemove", handleMouseMove, true);
                        document.removeEventListener("mouseup", handleMouseUp, true);
                    };

                    const setupLabelDragHandlers = () => {
                        const tooltip = labelMarker.getTooltip();
                        if (tooltip) {
                            const tooltipElement = (tooltip as any)._container as HTMLElement;
                            if (tooltipElement) {
                                tooltipElement.style.pointerEvents = "auto";

                                const labelElement = tooltipElement.querySelector(".gaia-label-text") as HTMLElement;
                                if (labelElement) {
                                    labelElement.style.pointerEvents = "auto";

                                    const handleMouseDown = (e: MouseEvent) => {
                                        if (drawModeRef.current === GAIA_DRAW_MODE.LABELS && e.button === 0) {
                                            e.stopPropagation();
                                            e.stopImmediatePropagation();
                                            e.preventDefault();

                                            isDragging = true;
                                            dragStartPos = { x: e.clientX, y: e.clientY };
                                            dragStartLatLng = labelMarker.getLatLng();

                                            if (map) {
                                                map.dragging.disable();
                                            }

                                            document.body.style.cursor = "grabbing";
                                        }
                                    };

                                    labelElement.addEventListener("mousedown", handleMouseDown, true);
                                }
                            }
                        }
                    };

                    (labelMarker as any)._setupLabelDragHandlers = setupLabelDragHandlers;

                    setTimeout(setupLabelDragHandlers, 50);
                }

                labelMarker.addTo(map);
            } catch (error) {
                console.error("Error creating editing geometry label:", error);
            }
        }

        return () => {
            if (editingGeometryLabelMarkerRef.current) {
                const marker = editingGeometryLabelMarkerRef.current;
                const dragCleanup = (marker as any)._labelDragCleanup;
                if (dragCleanup) {
                    dragCleanup();
                }
                if (map.hasLayer(marker)) {
                    map.removeLayer(marker);
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, geometryLabel, updateGeometryLabel, hasGeometry, workingGeometry]);

    useEffect(() => {
        MapViewHelper.setColorPalette(colorPalette || theme.palette || null);
    }, [colorPalette, theme.palette]);

    const onClonePreviousGeometryClicked = useCallback(
        (geometryJson27700: string) => {
            if (!map) {
                return;
            }
            suppressNextFitRef.current = true;
            const result = setGeometryFromGeoJSON(geometryJson27700, { fitBounds: false });
            if (result.success && onChange) {
                onChange(geometryJson27700, null);
            }
            setDrawMode(null);
        },
        [map, setGeometryFromGeoJSON, onChange]
    );

    useClonePreviousGeometry({
        map,
        drawMode,
        onClonePreviousGeometry,
        onGeometryClicked: onClonePreviousGeometryClicked,
        featureGroup,
    });

    useEffect(() => {
        if (drawMode === GAIA_DRAW_MODE.LABELS) {
            labelPositionsSnapshot.current.clear();
            labelMarkersRef.current.forEach((marker, id) => {
                labelPositionsSnapshot.current.set(id, marker.getLatLng());
            });
        }
    }, [drawMode]);

    const acceptLabels = useCallback(async () => {
        if (!updateGeometryLabel || !map) {
            setDrawMode(null);
            return;
        }

        const updates: { id: number; label: string }[] = [];

        labelMarkersRef.current.forEach((marker, id) => {
            const currentPosition = marker.getLatLng();
            const labelText = (marker as any)._labelText || "";
            const isDraggedInSession = (marker as any)._isDraggedInSession === true;

            const shouldSave = isDraggedInSession;

            if (shouldSave) {
                const pointFeatureCollection: GeoJSON.FeatureCollection = {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [currentPosition.lng, currentPosition.lat],
                            },
                        },
                    ],
                };
                const projected = GeometryProjector.projectTo27700(pointFeatureCollection);
                const epsg27700Coords = (projected.features[0].geometry as GeoJSON.Point).coordinates;
                const label: GaiaMapGeometryLabel = {
                    text: labelText,
                    position: [epsg27700Coords[0], epsg27700Coords[1]],
                };
                const stringifiedLabel = JSON.stringify(label);
                updates.push({
                    id,
                    label: stringifiedLabel,
                });
            }
        });

        if (updates.length > 0) {
            try {
                await updateGeometryLabel(updates);
            } catch (error) {
                console.error("Error saving label positions:", error);
            }
        }

        labelPositionsSnapshot.current.clear();
        setDrawMode(null);
    }, [updateGeometryLabel, map]);

    const cancelLabels = useCallback(() => {
        labelMarkersRef.current.forEach((marker, id) => {
            const originalPosition = labelPositionsSnapshot.current.get(id);
            if (originalPosition) {
                marker.setLatLng(originalPosition);
                (marker as any)._isDraggedInSession = false;
            }
        });

        labelPositionsSnapshot.current.clear();
        setDrawMode(null);
    }, []);

    const activatePrintPreview = useCallback(() => {
        setIsPrintPreviewActive(true);
    }, []);

    const closePrintPreview = useCallback(() => {
        setIsPrintPreviewActive(false);
    }, []);

    const onSnapshotRequested = useCallback(
        async (req: PrintMapRequest) => {
            if (onSnapshotRequest) {
                await onSnapshotRequest(req);
            }
        },
        [onSnapshotRequest]
    );

    const contextValue = useMemo<GaiaMapContext>(
        () => ({
            setDrawMode,
            drawMode,
            canUndo,
            canRedo,
            onUndo: handleUndo,
            onRedo: handleRedo,
            pushHistory,
            getUndoState,
            getRedoState,
            acceptDrawing,
            cancelDrawing,
            hasGeometry,
            hasLabels,
            acceptLabels,
            cancelLabels,
            readOnly,
            singleGeometryMode,
            onClone,
            center: currentCenter,
            centerMap,
            setGeometryFromGeoJSON,
            layerOnClick,
            workingGeometry,
            updateWorkingGeometry,
            featureGroupToWorkingGeometry,
            enabledToolTypes,
            onChange,
            disableUndoRedo,
            disableAcceptDrawing,
            onSelect,
            getFeatureInfo,
            wmsLayers,
            wmsUrl,
            activatePrintPreview,
            printLoading,
            startPrintLoading,
            stopPrintLoading,
            onMapBoxSizeRequested: onMapRequestHandler,
            mapBoxSize,
            pointCustomIcon,
            isFullScreen,
            setIsFullScreen,
            pointAsCircleMarker: pointAsCircleMarker || false,
            suppressNextFit,
            onClonePreviousGeometry,
        }),
        [
            drawMode,
            canUndo,
            canRedo,
            handleUndo,
            handleRedo,
            pushHistory,
            getUndoState,
            getRedoState,
            acceptDrawing,
            cancelDrawing,
            hasGeometry,
            hasLabels,
            acceptLabels,
            cancelLabels,
            readOnly,
            singleGeometryMode,
            onClone,
            currentCenter,
            centerMap,
            setGeometryFromGeoJSON,
            layerOnClick,
            workingGeometry,
            updateWorkingGeometry,
            featureGroupToWorkingGeometry,
            enabledToolTypes,
            onChange,
            disableUndoRedo,
            disableAcceptDrawing,
            onSelect,
            getFeatureInfo,
            wmsLayers,
            wmsUrl,
            activatePrintPreview,
            printLoading,
            startPrintLoading,
            stopPrintLoading,
            onMapRequestHandler,
            mapBoxSize,
            pointCustomIcon,
            isFullScreen,
            setIsFullScreen,
            pointAsCircleMarker,
            suppressNextFit,
            onClonePreviousGeometry,
        ]
    );

    return (
        <GaiaMapProvider value={contextValue}>
            <Box className="gaia__container" ref={gaiaMap} style={{ height: isFullScreen ? undefined : height }}>
                <GaiaMapRibbon customRibbonButtons={customRibbonButtons} showSnapshotButton={showSnapshotButton} />
                <Box className="gaia__map__container" style={{ position: "relative" }}>
                    <Box ref={mapContainer} className="gaia__map" style={{ height: isFullScreen ? undefined : `calc(${height}px - 80px)` }} />
                    <GaiaMapControls map={map} />
                    <GaiaMapPrintPreview isActive={isPrintPreviewActive} onClose={closePrintPreview} map={map} onSnapshotRequested={onSnapshotRequested} />
                </Box>
            </Box>
            <GaiaMapContent map={map} drawModeRef={drawModeRef} points={points} featureGroup={featureGroup} tempCircle={tempCircle} />
            {legend}
            {map && <MapMarkersPane {...internalClusterMarkersGroup} map={map} />}
        </GaiaMapProvider>
    );
});
